// src/utils/roleImpact.ts

/* =======================
   Types
======================= */

export type LeadershipRole = "function_head" | "business_head";
export type Scope = "single_bu" | "multiple_bu";
export type Geography = "local" | "regional" | "global";

export interface ApiRoleImpactItem {
  key: string;
  display_name: string;
}

export interface ParsedRoleImpact {
  isLeadership: boolean;
  role?: LeadershipRole;
  scope?: Scope;
  geography?: Geography;
  rawKey: string;
  displayName: string;
}

/* =======================
   Constants
======================= */

const NON_LEADERSHIP_KEYS = new Set([
  "team",
  "department",
  "corporate_or_global",
]);

/* =======================
   Parser
======================= */

export function parseRoleImpact(
  item: ApiRoleImpactItem
): ParsedRoleImpact {
  // Non-leadership roles
  if (NON_LEADERSHIP_KEYS.has(item.key)) {
    return {
      isLeadership: false,
      rawKey: item.key,
      displayName: item.display_name,
    };
  }

  // Leadership roles
  // Example key: function_head_single_bu_local
  const parts = item.key.split("_");

  return {
    isLeadership: true,
    role: `${parts[0]}_${parts[1]}` as LeadershipRole,
    scope: `${parts[2]}_${parts[3]}` as Scope,
    geography: parts[4] as Geography,
    rawKey: item.key,
    displayName: item.display_name,
  };
}

/* =======================
   Grouping for UI
======================= */

export function groupRoleImpactData(data: ApiRoleImpactItem[]) {
  const leadership: Record<
    LeadershipRole,
    Record<Scope, ParsedRoleImpact[]>
  > = {
    function_head: {
      single_bu: [],
      multiple_bu: [],
    },
    business_head: {
      single_bu: [],
      multiple_bu: [],
    },
  };

  const nonLeadership: ParsedRoleImpact[] = [];

  data.forEach(item => {
    const parsed = parseRoleImpact(item);

    if (!parsed.isLeadership) {
      nonLeadership.push(parsed);
      return;
    }

    leadership[parsed.role!][parsed.scope!].push(parsed);
  });

  return {
    leadership,
    nonLeadership,
  };
}
